import { useEffect, useState } from "react";
import { Header } from "semantic-ui-react";
import { convertToDateString } from "../../../Helpers";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import { GlobalConstants, LmsResidualHandlingOptions } from "../../constants/GlobalConstants";
import { FieldTypes } from "../../interfaces/Global";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { PayoffQuoteLoanCommentForm, PayoffQuoteSettlementStatementForm } from "../ui/LmsSharedForms";
import { PayoffQuoteUnitInlineTableEdit } from "../ui/LmsSharedTables";
import LmsTable from "../ui/LmsTable";

const PayoffTransactionsTable = () => (
  <LmsTable
    actionButtons={["edit", "export"]}
    apiSuffix="payoffquotes/payoff-transactions"
    columnLabelsDictionary={{
      borrowerOrganization: "Borrower",
      pcmarkedPaidOff: "PC Marked Paid Off",
      pcpayoffFundsSentDate: "Payoff Funds Sent Date",
      pcpayoutAmountSent: "Payout Amount Sent",
      lptransactionAmount: "LP Transaction Amount",
      lptransactionEffDate: "LP Transaction Eff Date"
    }}
    columnClickOptions={{
      loan: {
        path: "servicing-payoff-transactions/edit",
        idProp: "id",
      },
      documentFileName: { downloadPath: "document/download", idProp: "documentLogId" },
    }}
  />
);

const handleDisplayRofr = (formValue) => {
  if (formValue.includeRofr || formValue.rofrpercent || formValue.roframount || formValue.customizeRofr || formValue.rofrpending || formValue.rofrcharged || formValue.rofrcustomAmount) return true;
  return false;
};

const editRevolverPayoffTransactionFormField = (form: BCFormController, propertyUnitReconveyance) => {
  if (!form?.initialValues?.miniLoanPayoffQuoteObjectUid
    && !form?.initialValues?.miniLoanPayoffQuoteObjectU) {
    form.addDividerField({ dividerText: "Revolver" });
    form.addGroupFields([
      form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
      form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
        name: "specialPurposeEntityId",
        dependencyField: "borrowerOrganizationId",
        disabled: true
      }),
      form.getDisplayField("Loan", { name: "loanDisplay" })
    ]);

    form.addGroupFields([
      form.getDropdownField("Fund", "metadata/list-organization-funds", { 
        name: "fundOrganizationId", 
        required: true,
        disabled: true
      }),
      form.getDisplayField("Bank", { name: "bankInformationDisplay", disabled: true })
    ]);

    form.addGroupFields([
      form.getCalendarField("Payout Request Date", { disabled: true }),
      form.getCalendarField("Valid Through Date", { disabled: true })
    ]);
  }

  if (form?.initialValues?.isPartialQuote) {
    const unitObject = form?.initialValues?.payoffQuoteMiniLoanPayoffQuotes[0].miniLoanPayoffQuoteObjectU;
    form.addDividerField({ dividerText: "Unit" });
    form.addGroupFields([
      form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { value: unitObject.loan.borrowerOrganizationId , disabled: true }),
      form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
        value: unitObject.loan.specialPurposeEntityId,
        disabled: true
      }),
      form.getDisplayField("Loan", { name: "loanDisplay" }),
      form.getDisplayField("Escrow #", { value: unitObject.payoffEscrowNo })
    ]);
  
    form.addGroupFields([
      form.getDropdownField("Fund", "metadata/list-organization-funds", { 
        value: unitObject.fundOrganizationId, 
        required: true,
        disabled: true
      }),
      form.getDisplayField("Bank", { name: "bankInformationDisplay", disabled: true }),
      form.getCalendarField("Payout Request Date", { disabled: true }),
      form.getCalendarField("Valid Through Date", { value: unitObject.validThroughDate, disabled: true })
    ]);

    form.addGroupFields([
      form.getDisplayField("Refi?", { name: "refi" }),
      form.getCurrencyField("Original Loan Amount", { value: unitObject.loan.loanDetail.loanAmountOriginal , fieldType: "dollar", disabled: true }),
      form.getDisplayField("Days In Extension", { value: unitObject.daysInExtension }),
      form.getBlankField()
    ]);

    const propertyAddress = propertyUnitReconveyance ? `${propertyUnitReconveyance.loanProperty} Unit # ${propertyUnitReconveyance.unitNumber}`: "";
    form.addCenteredGroup([
      form.getDisplayField("Property Address", { value: propertyAddress })
    ]);

    form.addGroupFields([
      form.getCheckboxField("Include Sweep", { isDisabledOn: { netProceedsOrSweepOrFlat: "Flat"} }),
      form.getCurrencyField("Sweep Amount", { 
        value: unitObject.sweepAmount,
        isDisabledOn: { includeSweep: true }, 
        isDisabledOnOperation: "notequal", 
        fieldType: "dollar", 
        disabled: true 
      }),
      form.getDisplayField("Sweep %", { 
        value: unitObject.sweepPercentage,
        fieldType: "float" 
      }),
      form.getCurrencyField("Sweep Amount Applicable", { 
        value: unitObject.sweepAmountApplicable,
        isDisabledOn: { includeSweep: true }, 
        isDisabledOnOperation: "notequal", 
        disabled: true 
      }),
    ]);
  
    form.addGroupFields([
      form.getCheckboxField("Include Additional Interest", { value: unitObject.includeAdditionalInterest, disabled: true }),
      form.getDropdownField("Additional Interest Basis", "metadata/list-additional-interest-basis", { disabled: true, value: unitObject.additionalInterestBasisId }),
      form.getCurrencyField("Additional Interest Floor", { disabled: true, value: unitObject.additionalInterestFloor }),
      form.getCheckboxField("Apply Extension Fee", { value: unitObject.ApplyExtensionFee }),
    ]);

    form.addGroupFields([
      form.getCurrencyField("Reconveyance Fee", { 
        value: unitObject.reconveyanceFee, 
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal" 
      }),
      form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { 
        name: "transactionTypeId", 
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal"
      }),
      form.getCurrencyField("Additional Payoff Fees Amount", { 
        name: "additionalPayoffFee", 
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal"
      }),
      form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: unitObject.chargePayoffFee ? true : false })
    ]);

    form.addGroupFields([
      form.getCurrencyField("Charge Payoff Fee", { 
        value: unitObject.chargePayoffFee,
        isDisabledOn: { chargePayoffFeeCheckbox: true }, 
        isDisabledOnOperation: "notequal", 
      }),
      form.getCurrencyField("Reduce Payoff Amount", { value: unitObject.reducePayoffAmount, disabled: true }),
      form.getCheckboxField("Reduce By Remaining Budget", { value: unitObject.reduceByRemainingBudget, disabled: true }),
      form.getCurrencyField("Remaining Budget", { value: unitObject.remainingBudget, disabled: true })
    ]);

    form.addGroupFields([
      form.getCheckboxField("Apply Extension Credit", { value: unitObject.applyExtensionCredit ,disabled: true }),
      form.getCurrencyField("Extension Credit", { value: unitObject.extensionCredit ,disabled: true }),
      form.getBlankField(),
      form.getBlankField()
    ]);

    if (form?.initialValues && handleDisplayRofr(unitObject)) {
      form.addGroupFields([
        form.getCheckboxField("Include ROFR", { disabled: true, value: unitObject.includeRofr, name: "includeRofr" }),
        form.getDisplayField("ROFR Percent", { disabled: true, fieldType: "float", value: unitObject.rofrpercent }),
        form.getCurrencyField("ROFR Amount", {
          isDisabledOn: { includeRofr: true }, 
          isDisabledOnOperation: "notequal", 
          value: unitObject.roframount
        })
      ]);
  
      form.addGroupFields([
        form.getCheckboxField("Customize ROFR", { 
          value: unitObject.customizeRofr,
          isHidden: { includeRofr: true }, 
          isHiddenOperation: "notequal"
        }),
        form.getCurrencyField("ROFR Custom Amount", { 
          value: unitObject.rofrcustomAmount,
          isHidden: { includeRofr: true }, 
          isHiddenOperation: "notequal",
          isDisabledOn: { customizeRofr: true }, 
          isDisabledOnOperation: "notequal", 
        }),
        form.getCurrencyField("Revolver Adjustment Amount", { disabled: true, fieldType: "float", value: unitObject.revolverAdjustmentAmount }),
      ]);
    }

    form.addGroupFields([
      form.getCurrencyField("Total Payout", { value: unitObject.payoffQuoteAmount, disabled: true }),
      form.getTextareaField("Partial Deed Release Option", { name: "partialDeedReleaseOption", disabled: true, width: 15}),
      form.getCurrencyField("Partial Deed Release Amount", { value: unitObject.partialDeedReleaseAmount, disabled: true }),
    ]);

    form.addGroupFields([
      form.getTextareaField("Sweep Provision", { value: unitObject.loan.loanClosing.sweepProvision, disabled: true }),
      form.getTextareaField("Partial Deed Release Provision", { value: unitObject.loan.loanClosing.partialDeedReleaseProvision, disabled: true }),
    ]);
    
    form.addCenteredGroup([form.getTextareaField("Comments / Instructions", { value: unitObject.description, disabled: true  })]);
  }
  else if (form?.initialValues?.revolverPayoffQuoteId === null 
    && form?.initialValues?.isPartialQuote === false 
    && form?.initialValues?.miniLoanPayoffQuoteObjectUid
    && form?.initialValues?.miniLoanPayoffQuoteObjectU) {
    form.addDividerField({ dividerText: "Revolver" });
    form.addGroupFields([
      form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
      form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
        name: "specialPurposeEntityId",
        dependencyField: "borrowerOrganizationId",
        disabled: true
      }),
      form.getDisplayField("Loan", { name: "loanDisplay" }),
      form.getDisplayField("Escrow #", { name: "payoffEscrowNo" })
    ]);

    form.addGroupFields([
      form.getDropdownField("Fund", "metadata/list-organization-funds", { 
        name: "fundOrganizationId", 
        required: true,
        disabled: true
      }),
      form.getDisplayField("Bank", { name: "bankInformationDisplay", disabled: true }),
      form.getCalendarField("Payout Request Date", { disabled: true }),
      form.getCalendarField("Valid Through Date", { disabled: true })
    ]);

    form.addGroupFields([
      form.getDisplayField("Refi?", { name: "refi" }),
      form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
      form.getDisplayField("Days In Extension"),
      form.getBlankField()
    ]);

    form.addGroupFields([
      form.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", required: true, disabled: true }),
      form.getInputField("Current Interest Rate", {
        name: "loanServicingInterestRateCurrent",
        disabled: true,
        required: true,
        fieldType: GlobalConstants.InputTypes.FLOAT as FieldTypes,
      }),
      form.getCurrencyField("Interest Reserve Balance", { required: true, disabled: true }),
    ]);
  
    let pdPeriod1;
    if (form?.initialValues?.pdStartDate1 && form?.initialValues?.pdEndDate1) {
      pdPeriod1 = `${convertToDateString(form?.initialValues?.pdStartDate1, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate1, "lmsdisplaydateforfieldname")}`;
    }
  
    let pdPeriod2;
    if (form?.initialValues?.pdStartDate2 && form?.initialValues?.pdEndDate2) {
      pdPeriod2 = `${convertToDateString(form?.initialValues?.pdStartDate2, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate2, "lmsdisplaydateforfieldname")}`;
    }

    form.addGroupFields([
      form.getCurrencyField(pdPeriod1 ? pdPeriod1 : "Interest Pdstart Pdend1", { name: "interestPdstartPdend1", required: true, disabled: true }),
      form.getCurrencyField(pdPeriod2 ? pdPeriod2 : "Interest Pdstart Pdend2", { name: "interestPdstartPdend2", required: true, disabled: true }), 
      form.getBlankField()
    ]);

    form.addGroupFields([
      form.getInputField("Per Diem Interest", { required: true }),
      form.getCheckboxField("Prorate Extension", { 
        inline: true,
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal"
       }),
      form.getCurrencyField("Extension Fee", { disabled: true }),
      form.getCurrencyField("Per Diem Extension", { isDisabledOn: { markedPaidOff: true }, isDisabledOnOperation: "equal" }),
    ]);
  
    form.addGroupFields([
      form.getCheckboxField("Include Sweep", { 
        inline: true, 
        isDisabledOn: { netProceedsOrSweepOrFlat: "Flat"}  
      }),
      form.getCurrencyField("Sweep Amount", { 
        isDisabledOn: { includeSweep: true }, 
        isDisabledOnOperation: "notequal", 
        fieldType: "dollar", 
        disabled: true 
      }),
      form.getDisplayField("Sweep %", { 
        name: "sweepPercentage", 
        fieldType: "float" 
      }),
      form.getDisplayField("Sweep Amount Applicable", { 
        name: "sweepAmountApplicable", 
        isDisabledOn: { includeSweep: true }, 
        isDisabledOnOperation: "notequal", 
        fieldType: "dollar" 
      }),
    ]);
  
    form.addGroupFields([
      form.getDropdownDisplayField("Additional Interest Basis"),
      form.getDisplayField("Additional Interest Floor", { fieldType: "dollar" }),
      form.getDisplayField("Additional Interest %", { name: "additionalInterestRate", fieldType: "float" }),
      form.getCurrencyField("Additional Interest", { name: "additionalInterestAmount", disabled: true }),
    ]);

    form.addGroupFields([
      form.getCurrencyField("Reconveyance Fee", { required: true }),
      form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { 
        name: "transactionTypeId", 
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal" 
      }),
      form.getCurrencyField("Additional Payoff Fees Amount", { 
        name: "additionalPayoffFee", 
        isDisabledOn: { markedPaidOff: true },
        isDisabledOnOperation: "equal"
      }),
      form.getCurrencyField("Late Fee Amount", { disabled: true }),
    ]);
  
    form.addGroupFields([
      form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false }),
      form.getInputField("Charge Payoff Fee", { 
        isDisabledOn: { chargePayoffFeeCheckbox: true }, 
        isDisabledOnOperation: "notequal", 
      }),
      form.getCurrencyField("Previous Fee", { disabled: true })
    ]);
  
    if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
      form.addGroupFields([
        form.getCheckboxField("Include ROFR", { disabled: true, inline: true, name: "includeRofr" }), 
        form.getInputField("ROFR Percent", { disabled: true, fieldType: "float", name: "rofrpercent" }),
        form.getCurrencyField("ROFR Amount", {
          isDisabledOn: { includeRofr: true }, 
          isDisabledOnOperation: "notequal", 
          name: "roframount"
        })
      ]);
      
      form.addGroupFields([
        form.getCheckboxField("Customize ROFR", { 
          name: "customizeRofr",
          isHidden: { includeRofr: true }, 
          isHiddenOperation: "notequal"
        }),
        form.getCurrencyField("ROFR Custom Amount", { 
          name: "rofrcustomAmount",
          isHidden: { includeRofr: true }, 
          isHiddenOperation: "notequal",
          isDisabledOn: { customizeRofr: true }, 
          isDisabledOnOperation: "notequal", 
        }),
       form.getCurrencyField("Total Payout", { name: "payoffQuoteAmount", disabled: true })
      ]);
    }

    form.addCenteredGroup([form.getTextareaField("Comments / Instructions", { name: "description" })]);
    form.addDividerField({ dividerText: "Mini Loan" });
    // TODO: continue, reference sprint work 13788, test with 72901M
  }

  form.addDividerField({ dividerText: "" });
  form.addGroupFields([
    form.getCalendarField("Payout Date", { isDisabledOn: { markedPaidOff: true }, isDisabledOnOperation: "equal" }),
    form.getCurrencyField("Total Payout", { name: "revolverTotalAmount", disabled: true }),
    form.getCurrencyField("Payout Received", { name: "payoutReceived" }),
    form.getCurrencyField("Payout Residual", { name: "payoutResidual", disabled: true })
  ]);

  form.addGroupFields([
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions, { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual > 0
    }),
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions.slice(0, LmsResidualHandlingOptions.length-1), { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual <= 0
    }),
    form.getCurrencyField("Outstanding Interest And Fee Apply", { name: "outstandingInterestAndFees", disabled: true }),
    form.getCurrencyField("", { name: "outstandingInterestAndFees" }),
    form.getBlankField()
  ]);

  return form;
};

const editPartialPayoffTransactionFormField = (
  form: BCFormController,
  propertyUnitReconveyance: any[],
) => {
  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addComponent(<Header size="small" color="red" textAlign="center" className="lms-form-top-header">This loan has ROFR Language</Header>); 
  }

  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
      name: "specialPurposeEntityId",
      dependencyField: "borrowerOrganizationId",
      disabled: true
    }),
    form.getDisplayField("Loan", { name: "loanDisplay" }),
    form.getDisplayField("Escrow #", { name: "payoffEscrowNo" })
  ]);

  form.addGroupFields([
    form.getDropdownField("Fund", "metadata/list-organization-funds", { 
      name: "fundOrganizationId", 
      required: true,
      disabled: true
    }),
    form.getDisplayField("Bank", { name: "bankInformationDisplay", disabled: true }),
    form.getCalendarField("Payout Date", { required: true, isDisabledOn: { markedPaidOff: true }, isDisabledOnOperation: "equal" }),
    form.getCalendarField("Valid Through Date", { disabled: true })
  ]);

  form.addGroupFields([
    form.getDisplayField("Refi?", { name: "refi" }),
    form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
    form.getDisplayField("Days In Extension"),
    form.getBlankField()
  ]);

  if (propertyUnitReconveyance) {
    form.addComponent(
      <LmsTable
        data={propertyUnitReconveyance.find(unit => unit.id === form?.initialValues?.loanPropertyUnitReconveyanceId)}
        displayOnlyProps={["unitNumber", "loanProperty", "city", "state", "zip", "actualSalesPrice"]}
        columnLabelsDictionary={{unitNumber: "Unit"}}
        isDisabled
      />
    );
  }

  form.addGroupFields([
    form.getTextareaField("Sweep Provision", { disabled: true }),
    form.getTextareaField("Partial Deed Release Provision", { 
      name: "loanClosingPartialDeedReleaseProvision",
      disabled: true
    }),
  ]);

  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { 
      inline: true, 
      isDisabledOn: { netProceedsOrSweepOrFlat: "Flat"} 
    }),
    form.getCurrencyField("Sweep Amount", { 
      isDisabledOn: { includeSweep: true }, 
      isDisabledOnOperation: "notequal", 
      fieldType: "dollar", 
      disabled: true 
    }),
    form.getDisplayField("Sweep %", { 
      name: "sweepPercentage", 
      fieldType: "float" 
    }),
    form.getCurrencyField("Sweep Amount Applicable", { 
      name: "sweepAmountApplicable", 
      isDisabledOn: { includeSweep: true }, 
      isDisabledOnOperation: "notequal", 
      disabled: true 
    }),
  ]);

  form.addGroupFields([
    form.getCheckboxField("Include Additional Interest", { disabled: true }),
    form.getDropdownField("Additional Interest Basis", "metadata/list-additional-interest-basis", { disabled: true, required: true, name: "additionalInterestBasisId" }),
    form.getCurrencyField("Additional Interest Floor", { disabled: true })
  ]);

  form.addGroupFields([
    form.getInputField("Reconveyance Fee", { required: true, disabled: true }),
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { 
      name: "transactionTypeId", 
      isDisabledOn: { markedPaidOff: true },
      isDisabledOnOperation: "equal"
    }),
    form.getCurrencyField("Additional Payoff Fees Amount", { 
      name: "additionalPayoffFee", 
      isDisabledOn: { markedPaidOff: true },
      isDisabledOnOperation: "equal"
    }),
  ]);

  form.addGroupFields([
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false }),
    form.getInputField("Charge Payoff Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal", 
      name: "payoffProcessingFee"
    }),
    form.getCurrencyField("Previous Fee", { disabled: true })
  ]);

  form.addGroupFields([
    form.getInputField("Reduce Payoff Amount"),
    form.getCheckboxField("Reduce By Remaining Budget", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false }),
    form.getCurrencyField("Remaining Budget", { disabled: true })
  ]);

  form.addGroupFields([
    form.getCheckboxField("Apply Extension Credit", { disabled: true }),
    form.getCurrencyField("Extension Credit", { disabled: true }),
    form.getBlankField()
  ]);

  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addGroupFields([
      form.getCheckboxField("Include ROFR", { disabled: true, inline: true, name: "includeRofr" }),
      form.getInputField("ROFR Percent", { disabled: true, fieldType: "float", name: "rofrpercent" }),
      form.getCurrencyField("ROFR Amount", {
        isDisabledOn: { includeRofr: true }, 
        isDisabledOnOperation: "notequal", 
        name: "roframount"
      })
    ]);

    form.addGroupFields([
      form.getCheckboxField("Customize ROFR", { 
        name: "customizeRofr",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal"
      }),
      form.getCurrencyField("ROFR Custom Amount", { 
        name: "rofrcustomAmount",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        isDisabledOn: { customizeRofr: true }, 
        isDisabledOnOperation: "notequal", 
      }),
      form.getBlankField()
    ]);

    form.addGroupFields([
      form.getCurrencyField("Outstanding Interest And Fee Apply", { disabled: true }),
      form.getDropdownField("Residual Handling", LmsResidualHandlingOptions, { 
        name: "payoffResidualHandlingOption",
        isHiddenFunc: ({ inputs }) => inputs?.payoutResidual > 0
      }),
      form.getDropdownField("Residual Handling", LmsResidualHandlingOptions.slice(0, LmsResidualHandlingOptions.length-1), { 
        name: "payoffResidualHandlingOption",
        isHiddenFunc: ({ inputs }) => inputs?.payoutResidual <= 0
      }),
      form.getDisplayField("Principal Payment Apply", { name: "principalPaymentApply" }),
    ]);
  }

  form.addGroupFields([
    form.getCurrencyField("Total Payout", { name: "payoffQuoteAmount", disabled: true }),
    form.getCurrencyField("Payout Received", { name: "payoutReceived" }),
    form.getCurrencyField("Payout Residual", { name: "payoutResidual", disabled: true })
  ]);
  
  form.addGroupFields([
    form.getComponent((options) =>
      <LmsTable
        apiSuffix={form?.values?.id ? `payoffquotes/payoff-temp-comments/${form?.values?.id}`: ""}
        displayOnlyProps={["commentSubjectDisplay", "description", "createdOn", "createdBy"]}
        columnLabelsDictionary={{commentSubjectDisplay: "Subject"}}
        title="Payoff comments"
        refreshDataDependency={options?.inputs?.lastAddedCommentId}
        compact
        isDisabled
      />,
    { width: 8 }),
    form.getComponent(() => {
      return <>
        {form?.values?.commentsHtml?.length >= 1 && <div className="lms-comments-html-div">
          {form?.values?.commentsHtml?.map((comment) => {
            return <p className="lms-comments-html">{comment}</p>;
          })}
        </div>}
      </>;
    }, { width: 4 }),
    form.getButtonField("Add Comment", undefined, {
      modalContent: (setIsAddCommentModalOpen, options) => (
        <PayoffQuoteLoanCommentForm
          payoffQuoteOptions={options}
          id={form?.values?.loanId} 
          setIsAddCommentModalOpen={setIsAddCommentModalOpen} 
        />
      ),
      modalSize: "large",
      width: 2 
    })
  ], { blankWidth: 1 });

  form.addGroupFields([
    form.getTextareaField("Partial Deed Release Option", { disabled: true, width: 15}),
    form.getCurrencyField("Partial Deed Release Amount", { disabled: true }),
  ]);

  form.addGroupFields([
    form.getCurrencyField("Outstanding Interest And Fee Apply", { name: "outstandingInterestAndFees", disabled: true }),
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions, { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual > 0
    }),
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions.slice(0, LmsResidualHandlingOptions.length-1), { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual <= 0
    }),
    form.getDisplayField("Principal Payment", { name: "principalPaymentApply" }),
  ]);

  form.addGroupFields([
    form.getDropdownField("PDR Bank Information", "payoffquotes/list-fund-bank-info", { 
      name: "pdrBankInformationId",
      dependencyField: "fundOrganizationId"
    }),
    form.getCurrencyField("PDR Transaction Amount"),
    form.getCalendarField("PDR Transaction Effective Date")
  ]);

  form.addCenteredGroup([form.getTextareaField("Comments / Instructions", { name: "description" })]);
  form.addCenteredGroup([
    form.getButtonField("Settlement Statement / Escrow Request", undefined, {
      modalContent: (setIsModalOpen, options) => (
        <PayoffQuoteSettlementStatementForm
          id={form?.values?.loanId} 
          setIsPayoffQuoteDocumentModalOpen={setIsModalOpen} 
          payoffQuoteOptions={options}
        />
      )
    }),
    form.getComponent((options) => {
      return <div className="lms-flex-start-container">
        {options?.metaData?.uploadedSettlementStatements?.map((statement, i) => {
          return <a className="lms-download-form-field-link" key={`lms-download-form-field-link${i}`} onClick={() => {
            return options.handleDownloadFile(`payoffquotes/download-settlement-statements/${statement?.id}`, { fileName : statement?.assetFile?.name });
          }}>{statement?.assetFile?.name}</a>;
        })}
      </div>;
    })
  ]);

  return form;
};

const editPayoffTransactionFormField = (
  form: BCFormController
) => {
  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addComponent(<Header size="small" color="red" textAlign="center" className="lms-form-top-header">This loan has ROFR Language</Header>); 
  }

  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
      name: "specialPurposeEntityId",
      dependencyField: "borrowerOrganizationId",
      disabled: true
    }),
    form.getDisplayField("Loan", { name: "loanDisplay" }),
    form.getDisplayField("Escrow #", { name: "payoffEscrowNo" })
  ]);

  form.addGroupFields([
    form.getDropdownField("Fund", "metadata/list-organization-funds", { 
      name: "fundOrganizationId", 
      required: true,
      disabled: true
    }),
    form.getDisplayField("Bank", { name: "bankInformationDisplay", disabled: true }),
    form.getCalendarField("Payout Date", { 
      required: true, 
      maxDate: 0,
      isDisabledOn: { markedPaidOff: true }, 
      isDisabledOnOperation: "equal"
     }),
    form.getCalendarField("Valid Through Date", { disabled: true })
  ]);

  form.addGroupFields([
    form.getDisplayField("Refi?", { name: "refi" }),
    form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
    form.getDisplayField("Days In Extension"),
    form.getBlankField()
  ]);

  const formatSalePriceDataFunc = (data, options) => {
    if (data?.actualSalePrices) {
      options.setInputs((prevInputs) => { return { ...prevInputs, partialDeedReleaseAmount: data?.partialDeedReleaseAmount }; });
      if (!options?.inputs?.loanDetailLoanSubTypeName?.includes("Refi")) {
        return data?.actualSalePrices.map(item => {return { unit: item.unit, appraisedValue: item.actualSalePrice };});
      }
  
      return data?.actualSalePrices;
    }
  
    return [];
  };

  form.addComponent((options) => 
    <LmsTable 
      key="actual-sale-price-table"
      apiSuffix={options?.inputs?.loanId ? `payoffquotes/property-unit-reconveyance/${options?.inputs?.loanId}` : ""}
      isDisabled
      formatDataFunc={(data) => formatSalePriceDataFunc(data, options)}
      refreshDataDependency={options?.inputs?.lastUpdatedUnitId}
      {...(form?.values?.loanDetailLoanSubTypeName?.includes("Refi") === false ? { columnLabelsDictionary: { actualSalesPrice: "Appraised Value" }} : {})}

    />
  );

  form.addCenteredGroup([form.getButtonField("Edit", undefined, {
    modalContent: (options) => <PayoffQuoteUnitInlineTableEdit options={options} />
  })]);
  
  form.addGroupFields([
    form.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", required: true, disabled: true }),
    form.getInputField("Current Interest Rate", {
      name: "loanServicingInterestRateCurrent",
      disabled: true,
      required: true,
      fieldType: GlobalConstants.InputTypes.FLOAT as FieldTypes,
    }),
    form.getCurrencyField("Interest Reserve Balance", { required: true, disabled: true }),
  ]);

  let pdPeriod1;
  if (form?.initialValues?.pdStartDate1 && form?.initialValues?.pdEndDate1) {
    pdPeriod1 = `${convertToDateString(form?.initialValues?.pdStartDate1, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate1, "lmsdisplaydateforfieldname")}`;
  }

  let pdPeriod2;
  if (form?.initialValues?.pdStartDate2 && form?.initialValues?.pdEndDate2) {
    pdPeriod2 = `${convertToDateString(form?.initialValues?.pdStartDate2, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate2, "lmsdisplaydateforfieldname")}`;
  }

  form.addGroupFields([
    form.getCurrencyField(pdPeriod1 ? pdPeriod1 : "Interest Pdstart Pdend1", { name: "interestPdstartPdend1", required: true, disabled: true }),
    form.getCurrencyField(pdPeriod2 ? pdPeriod2 : "Interest Pdstart Pdend2", { name: "interestPdstartPdend2", required: true, disabled: true }), 
    form.getBlankField()
  ]);

  form.addGroupFields([
    form.getInputField("Per Diem Interest", { disabled: true }),
    form.getCheckboxField("Prorate Extension", { inline: true, isDisabledOn: { markedPaidOff: true }, isDisabledOnOperation: "equal" }),
    form.getCurrencyField("Extension Fee", { required: true, disabled: true }),
    form.getCurrencyField("Per Diem Extension", { isDisabledOn: { markedPaidOff: true }, isDisabledOnOperation: "equal" }),
  ]);

  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { 
      inline: true, 
      isDisabledOn: { netProceedsOrSweepOrFlat: "Flat"}  
    }),
    form.getCurrencyField("Sweep Amount", { 
      isDisabledOn: { includeSweep: true }, 
      isDisabledOnOperation: "notequal", 
      fieldType: "dollar", 
      disabled: true 
    }),
    form.getDisplayField("Sweep %", { 
      name: "sweepPercentage", 
      fieldType: "float" 
    }),
    form.getDisplayField("Sweep Amount Applicable", { 
      name: "sweepAmountApplicable", 
      isDisabledOn: { includeSweep: true }, 
      isDisabledOnOperation: "notequal", 
      fieldType: "dollar" 
    }),
  ]);

  form.addGroupFields([
    form.getDropdownDisplayField("Additional Interest Basis"),
    form.getDisplayField("Additional Interest Floor", { fieldType: "dollar" }),
    form.getDisplayField("Additional Interest %", { name: "additionalInterestRate", fieldType: "float" }),
    form.getCurrencyField("Additional Interest", { name: "additionalInterestAmount", disabled: true }),
  ]);

  form.addGroupFields([
    form.getCurrencyField("Reconveyance Fee", { required: true }),
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { 
      name: "transactionTypeId", 
      isDisabledOn: { markedPaidOff: true },
      isDisabledOnOperation: "equal"
    }),
    form.getCurrencyField("Additional Payoff Fees Amount", { 
      name: "additionalPayoffFee", 
      isDisabledOn: { markedPaidOff: true },
      isDisabledOnOperation: "equal"
    }),
    form.getCurrencyField("Late Fee Amount", { disabled: true }),
  ]);

  form.addGroupFields([
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false }),
    form.getInputField("Charge Payoff Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal", 
    }),
    form.getCurrencyField("Previous Fee", { disabled: true })
  ]);

  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addGroupFields([
      form.getCheckboxField("Include ROFR", { disabled: true, inline: true, name: "includeRofr" }),
      form.getInputField("ROFR Percent", { disabled: true, fieldType: "float", name: "rofrpercent" }),
      form.getCurrencyField("ROFR Amount", {
        isDisabledOn: { includeRofr: true }, 
        isDisabledOnOperation: "notequal", 
        name: "roframount"
      })
    ]);
    
    form.addGroupFields([
      form.getCheckboxField("Customize ROFR", { 
        name: "customizeRofr",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal"
      }),
      form.getCurrencyField("ROFR Custom Amount", { 
        name: "rofrcustomAmount",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        isDisabledOn: { customizeRofr: true }, 
        isDisabledOnOperation: "notequal", 
      })
    ]);
  }

  form.addGroupFields([
    form.getCurrencyField("Total Payout", { name: "payoffQuoteAmount", disabled: true }),
    form.getCurrencyField("Payout Received", { name: "payoutReceived" }),
    form.getCurrencyField("Payout Residual", { name: "payoutResidual", disabled: true })
  ]);

  form.addGroupFields([
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions, { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual > 0
    }),
    form.getDropdownField("Residual Handling", LmsResidualHandlingOptions.slice(0, LmsResidualHandlingOptions.length-1), { 
      name: "payoffResidualHandlingOption",
      isHiddenFunc: ({ inputs }) => inputs?.payoutResidual <= 0
    }),
    form.getDropdownField("Loan Sub Status", "metadata/list-paid-off-loan-sub-statuses", { name: "loanSubStatusId" }),
  ]);

  form.addGroupFields([
    form.getComponent((options) =>
      <LmsTable
        apiSuffix={form?.values?.id ? `payoffquotes/payoff-temp-comments/${form?.values?.id}`: ""}
        displayOnlyProps={["commentSubjectDisplay", "description", "createdOn", "createdBy"]}
        columnLabelsDictionary={{commentSubjectDisplay: "Subject"}}
        title="Payoff comments"
        refreshDataDependency={options?.inputs?.lastAddedCommentId}
        compact
        isDisabled
      />,
    { width: 8 }),
    form.getComponent(() => {
      return <>
        {form?.values?.commentsHtml?.length >= 1 && <div className="lms-comments-html-div">
          {form?.values?.commentsHtml?.map((comment) => {
            return <p className="lms-comments-html">{comment}</p>;
          })}
        </div>}
      </>;
    }, { width: 4 }),
    form.getButtonField("Add Comment", undefined, {
      modalContent: (setIsAddCommentModalOpen, options) => (
        <PayoffQuoteLoanCommentForm
          payoffQuoteOptions={options}
          id={form?.values?.loanId} 
          setIsAddCommentModalOpen={setIsAddCommentModalOpen} 
        />
      ),
      modalSize: "large",
      width: 2 
    })
  ], { blankWidth: 1 });

  form.addCenteredGroup([form.getTextareaField("Comments / Instructions")]);
  form.addCenteredGroup([
    form.getButtonField("Settlement Statement / Escrow Request", undefined, {
      modalContent: (setIsModalOpen, options) => (
        <PayoffQuoteSettlementStatementForm
          id={form?.values?.loanId} 
          setIsPayoffQuoteDocumentModalOpen={setIsModalOpen} 
          payoffQuoteOptions={options}
        />
      )
    }),
    form.getComponent((options) => {
      return <div className="lms-flex-start-container">
        {options?.metaData?.uploadedSettlementStatements?.map((statement, i) => {
          return <a className="lms-download-form-field-link" key={`lms-download-form-field-link${i}`} onClick={() => {
            return options.handleDownloadFile(`payoffquotes/download-settlement-statements/${statement?.id}`, { fileName : statement?.assetFile?.name });
          }}>{statement?.assetFile?.name}</a>;
        })}
      </div>;
    })
  ]);

  return form;
};

const LmsServicingPayoffTransactionEditForm = ({ id }) => {
  const [{ data: payoffTransactionData, isLoading: isPayoffTransactionDataLoading, error, fetchData }] = useFetchData(`payoffquotes/initial-data-for-edit/${id}`);
  const [propertyUnitReconveyance, setPropertyUnitReconveyance] = useState(undefined as any);
  const handleDisplayFormField = () => {
    const myForm = new BCFormController(payoffTransactionData, { 
      values: { 
        id: id, 
        loanId: payoffTransactionData?.loanId, 
        payoutResidual: payoffTransactionData?.payoutResidual,
        markedPaidOff: payoffTransactionData?.markedPaidOff,
        commentsHtml: payoffTransactionData?.commentsHtml?.split("<br />")
      },
      allDropdownOptionsAreReady: !!payoffTransactionData
    });
    if (payoffTransactionData?.loanPropertyUnitReconveyanceIdCsv) {
      return editRevolverPayoffTransactionFormField(
        myForm, 
        propertyUnitReconveyance?.find(unit => unit.id === payoffTransactionData?.loanPropertyUnitReconveyanceIdCsv)
      );
    }
    else {
      if (payoffTransactionData?.isPartialQuote) {
        return editPartialPayoffTransactionFormField (
          myForm,
          propertyUnitReconveyance
        );
      }
      
      return editPayoffTransactionFormField(myForm);
    }
  };

  const myForm = handleDisplayFormField();
  

  const handleMarkUnitPaidOff = async (inputs, handleSubmit, useFormInstance) => {
    useFormInstance.initSubmittingStates(useFormInstance.buttonText); // TODO: test again after Sprint Work 13819
    handleSubmit("payoffquotes/transaction/mark-unit-paid-off", { 
      method: "POST", 
      data: { ...inputs, loanId: payoffTransactionData?.loanId }, 
      buttonText: useFormInstance.buttonText 
    });
  };

  const handleMarkPaidOff = async (inputs, handleSubmit, useFormInstance) => {
    useFormInstance.initSubmittingStates(useFormInstance.buttonText);
    handleSubmit("payoffquote/transaction/mark-loan-purchaser-paid-Off", {
      method: "POST", 
      data: { ...inputs, loanId: payoffTransactionData?.loanId }, 
      buttonText: useFormInstance.buttonText 
    });
  };

  const handleMarkLoanPurchaserPaidOff = async (inputs, handleSubmit, useFormInstance) => {
    useFormInstance.initSubmittingStates(useFormInstance.buttonText);
    handleSubmit("payoffquote/transaction/mark-loan-purchaser-paid-Off", {
      method: "POST", 
      data: { ...inputs, loanId: payoffTransactionData?.loanId }, 
      buttonText: useFormInstance.buttonText 
    });
  };

  const formActions = [
    "save", 
    (payoffTransactionData?.loanPropertyUnitReconveyanceId 
      ? { text: "Mark Unit Paid Off", icon: GlobalConstants.Icons.SAVE, handleClick: handleMarkUnitPaidOff }
      : { text: "Mark Paid Off", icon: GlobalConstants.Icons.SAVE, handleClick: handleMarkPaidOff, }
    ), 
    { text: "Mark Loan Purchaser Paid Off", icon: GlobalConstants.Icons.SAVE, handleClick: handleMarkLoanPurchaserPaidOff }, 
    // TODO: Ask Alex on what enable this button ^
    "close"
  ];

  const getTotalPayoffAndResidual = (inputs) => {
    let totalPayoff = payoffTransactionData.loanAmountCurrent + payoffTransactionData.interestReserveBalance 
      + payoffTransactionData.interestPdstartPdend1 + payoffTransactionData.interestPdstartPdend2 
      + payoffTransactionData.lateFeeAmount + inputs.reconveyanceFee
      + payoffTransactionData.additionalInterestAmount + payoffTransactionData.additionalInterestFloor;
    inputs.sweepAmountApplicable = parseFloat(inputs.sweepAmountApplicable || 0);
    inputs.payoutReceived = parseFloat(inputs.payoutReceived || 0);
    if (inputs.includeSweep) {
      totalPayoff += inputs.sweepAmountApplicable;
    }

    if (inputs.includeRofr && inputs.customizeRofr) {
      totalPayoff += parseFloat(inputs.rofrcustomAmount || 0);
    } else if (inputs.includeRofr) {
      totalPayoff = totalPayoff + parseFloat(inputs.roframount || 0) - payoffTransactionData.rofrcharged;
    }

    if (payoffTransactionData) {
      totalPayoff += parseFloat(payoffTransactionData.extensionAdjustmentAmount || 0);
      if (payoffTransactionData.payoffQuoteCrossCollateralLoans?.length > 0) {
        let totalPastDueAmount = 0;
        payoffTransactionData.payoffQuoteCrossCollateralLoans.map(crossCollateral => {
          if (crossCollateral.isSelected) {
            totalPastDueAmount += crossCollateral.pastDueAmount ? crossCollateral.pastDueAmount : 0;
          }
        });

        totalPayoff += totalPastDueAmount;
      }
    }

    if (inputs.includeSweep) {
      inputs.payoutResidual = inputs.payoutReceived - totalPayoff + inputs.sweepAmountApplicable;
    }
    else {
      inputs.payoutResidual = inputs.payoutReceived - totalPayoff;
    }

    inputs.totalPayoff = totalPayoff;
  };

  const onPayoffTranactionsFormChange = (options, lastUpdated) => {
    const inputCopy = {...options?.inputs};
    if (lastUpdated === "reconveyanceFee" || lastUpdated === "sweepAmountApplicable") {
      inputCopy[lastUpdated] = parseFloat(inputCopy[lastUpdated] || 0);
    }

    if (lastUpdated === "includeSweep" || lastUpdated === "sweepAmount") {
      if (inputCopy.includeSweep) {
        inputCopy.sweepAmountApplicable = (inputCopy.sweepAmount * inputCopy.sweepPercentage) / 100;
      }
      else {
        inputCopy.sweepAmount = 0;
        inputCopy.sweepAmountApplicable = "";
        inputCopy.customizeRofr = false;
        inputCopy.rofrCustomAmount = "";
      }
    }

    if (lastUpdated === "includeRofr" || lastUpdated === "customizeRofr") {
      if (inputCopy[lastUpdated] === false) {
        inputCopy.rofrCustomAmount = "";
      }
    }

    if (lastUpdated === "applyExtensionFee") {
      if (inputCopy?.payoffQuoteMiniLoanPayoffQuotes?.length) {
        inputCopy.payoffQuoteMiniLoanPayoffQuotes[0].miniLoanPayoffQuoteObjectU.applyExtensionCredit = true;
      }
    }

    getTotalPayoffAndResidual(inputCopy);
    options?.setInputs({ ...inputCopy });
  };

  useEffect(() => {
    if (payoffTransactionData) {
      if (payoffTransactionData?.loanId) {
        const getPropertyUnitReconveyance = async () => {
          const { data: units } = await fetchData(`payoffquotes/property-unit-reconveyance/${payoffTransactionData.loanId}`, { fetchMethod: "GET" });
          setPropertyUnitReconveyance(units);
        };
        
        getPropertyUnitReconveyance();
      }

      getTotalPayoffAndResidual(payoffTransactionData);
    }
  }, [payoffTransactionData]);

  return <LmsFormGenerator 
    myForm={myForm} 
    isLoading={isPayoffTransactionDataLoading} 
    error={error} 
    actions={formActions} 
    onFormChanged={onPayoffTranactionsFormChange}
  />;
};

const LmsServicingPayoffTransactions = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params);
  if (!isActionAllowed) {
    history.goBack();
  }

  return (<>
    {isMainPage && <PayoffTransactionsTable/>}
    {isEdit && <LmsServicingPayoffTransactionEditForm id={id}/>}
  </>);
};

export default LmsServicingPayoffTransactions;